import type * as THREE from 'three';
import type { Tool } from '../ui/tray';
import type { GroundPicker } from './verbs';
import type { SimClient } from '../app/simClient';
import type { Sfx } from '../audio/sfx';
import { ITEM } from '../sim/items';

/**
 * Gesture arbiter. Listens in the capture phase on a parent of the canvas so
 * it can claim gestures (scrub, ball slingshot) before camera-controls sees
 * the pointerdown; unclaimed gestures fall through to the camera. Taps are
 * detected passively (no movement = camera never pans anyway).
 */
const TAP_MS = 420; // phones tap slower than mice
const TAP_SLOP_PX = 12;
const PRESS_MS = 450;
const TOUCH_PICK_RADIUS = 2.4; // world units — a fingertip is not a cursor

interface GestureDeps {
  root: HTMLElement; // capture root, parent of the canvas
  canvas: HTMLCanvasElement; // the world surface — UI taps must never reach verbs
  picker: GroundPicker;
  getTool: () => Tool;
  creaturesMesh: THREE.InstancedMesh;
  population: () => number;
  sim: SimClient;
  sfx: Sfx;
  /** first meaningful use of a tool (discovery hook) */
  onToolUsed: (tool: Tool) => void;
  /** long-press on a creature */
  onInspect: (id: number) => void;
  /** hand-tap on open grass: the player's call sign */
  onCall: (x: number, z: number) => void;
  /** double-tap on grass: the player sings a dance up */
  onSong: (x: number, z: number) => void;
  /** rub stroke tick on a creature (for the purr) */
  onRub: (id: number) => void;
}

type Claim =
  | { kind: 'none' }
  | { kind: 'scrub'; pointerId: number; points: { x: number; z: number }[]; lastX: number; lastZ: number; lastSqueak: number; lastSend: number }
  | { kind: 'ballAim'; pointerId: number; startX: number; startZ: number; startPx: number; startPy: number; t0: number }
  | { kind: 'rub'; pointerId: number; creature: number; startPx: number; startPy: number; t0: number; rubbing: boolean; lastSend: number };

export function attachGestures(deps: GestureDeps): void {
  let claim: Claim = { kind: 'none' };
  let tap: { id: number; px: number; py: number; t0: number } | null = null;
  let pressTimer = 0;
  let pressConsumedPointer = -1; // a fired long-press must not also count as a tap
  let lastGrassTap: { px: number; py: number; x: number; z: number; time: number } | null = null;

  /** Precise triangle raycast first; else the nearest creature to the ground
   *  point within a fingertip radius. The egg always used the radius check —
   *  this gives creatures the same courtesy. */
  const pickCreatureAt = (clientX: number, clientY: number): number | null => {
    if (deps.population() === 0) return null;
    const exact = deps.picker.pickCreature(clientX, clientY, deps.creaturesMesh);
    if (exact !== null && exact < deps.population()) return exact;
    const g = deps.picker.pickGround(clientX, clientY);
    if (!g) return null;
    const near = deps.sim.nearestCreature(g.x, g.z, TOUCH_PICK_RADIUS);
    return near >= 0 ? near : null;
  };

  const endClaim = (): void => {
    claim = { kind: 'none' };
  };

  const cancelPress = (): void => {
    if (pressTimer !== 0) {
      window.clearTimeout(pressTimer);
      pressTimer = 0;
    }
  };

  deps.root.addEventListener(
    'pointerdown',
    (e: PointerEvent) => {
      if (e.target !== deps.canvas) {
        // the touch belongs to HUD chrome (tray, menu, windows) — a tool
        // switch must not also fire the previous tool at that spot
        cancelPress();
        tap = null;
        return;
      }
      if (claim.kind !== 'none') return; // one claimed gesture at a time
      const tool = deps.getTool();

      if (tool === 'sponge') {
        const g = deps.picker.pickGround(e.clientX, e.clientY);
        if (g && deps.sim.distToItem(ITEM.GRIME, g.x, g.z) < 1.4) {
          claim = {
            kind: 'scrub',
            pointerId: e.pointerId,
            points: [g],
            lastX: g.x,
            lastZ: g.z,
            lastSqueak: 0,
            lastSend: performance.now(),
          };
          e.stopPropagation(); // camera never sees this drag
          return;
        }
      }

      if (tool === 'ball') {
        const g = deps.picker.pickGround(e.clientX, e.clientY);
        if (g) {
          claim = {
            kind: 'ballAim',
            pointerId: e.pointerId,
            startX: g.x,
            startZ: g.z,
            startPx: e.clientX,
            startPy: e.clientY,
            t0: performance.now(),
          };
          e.stopPropagation();
          return;
        }
      }

      // unclaimed: candidate tap; camera-controls handles any drag
      tap = { id: e.pointerId, px: e.clientX, py: e.clientY, t0: performance.now() };

      if (tool === 'hand' && deps.population() > 0) {
        // touching a creature claims the pointer: a still hold inspects,
        // a moving hold RUBS (streamed strokes), a quick release pets —
        // the camera never pans from a touch that starts on a friend
        const creature = pickCreatureAt(e.clientX, e.clientY);
        if (creature !== null) {
          claim = {
            kind: 'rub',
            pointerId: e.pointerId,
            creature,
            startPx: e.clientX,
            startPy: e.clientY,
            t0: performance.now(),
            rubbing: false,
            lastSend: 0,
          };
          e.stopPropagation();
        }
        // long-press -> inspect card (cancelled if the rub starts moving)
        cancelPress();
        const { clientX, clientY, pointerId } = e;
        pressTimer = window.setTimeout(() => {
          pressTimer = 0;
          const id = pickCreatureAt(clientX, clientY);
          if (id !== null) {
            pressConsumedPointer = pointerId;
            deps.onInspect(id);
          }
        }, PRESS_MS);
      }
    },
    { capture: true },
  );

  deps.root.addEventListener(
    'pointermove',
    (e: PointerEvent) => {
      if (claim.kind === 'scrub' && e.pointerId === claim.pointerId) {
        e.stopPropagation();
        const g = deps.picker.pickGround(e.clientX, e.clientY);
        if (!g) return;
        const moved = Math.hypot(g.x - claim.lastX, g.z - claim.lastZ);
        if (moved < 0.12) return;
        const now = performance.now();
        claim.points.push(g);
        // squeak pitched to stroke speed, throttled
        if (now - claim.lastSqueak > 70) {
          deps.sfx.squeak(Math.min(1, moved / 0.9));
          claim.lastSqueak = now;
        }
        claim.lastX = g.x;
        claim.lastZ = g.z;
        if (claim.points.length >= 4 || now - claim.lastSend > 80) {
          deps.sim.scrub(claim.points.splice(0));
          claim.lastSend = now;
        }
      } else if (claim.kind === 'ballAim' && e.pointerId === claim.pointerId) {
        e.stopPropagation();
      } else if (claim.kind === 'rub' && e.pointerId === claim.pointerId) {
        e.stopPropagation();
        const moved = Math.hypot(e.clientX - claim.startPx, e.clientY - claim.startPy);
        if (moved > TAP_SLOP_PX) {
          claim.rubbing = true;
          cancelPress(); // it's a rub, not an inspect-hold
        }
        if (claim.rubbing) {
          const now = performance.now();
          if (now - claim.lastSend > 240) {
            claim.lastSend = now;
            deps.sim.stroke(claim.creature);
            deps.onRub(claim.creature);
          }
        }
      } else if (tap && e.pointerId === tap.id) {
        // moving past the slop turns a press into a camera drag
        if (Math.hypot(e.clientX - tap.px, e.clientY - tap.py) > TAP_SLOP_PX) cancelPress();
      }
    },
    { capture: true },
  );

  const onUp = (e: PointerEvent): void => {
    if (claim.kind === 'rub' && e.pointerId === claim.pointerId) {
      e.stopPropagation();
      cancelPress();
      const wasQuickTap =
        !claim.rubbing &&
        performance.now() - claim.t0 < TAP_MS &&
        e.pointerId !== pressConsumedPointer;
      if (wasQuickTap) deps.sim.pet(claim.creature);
      if (e.pointerId === pressConsumedPointer) pressConsumedPointer = -1;
      tap = null;
      endClaim();
      return;
    }
    if (claim.kind === 'scrub' && e.pointerId === claim.pointerId) {
      e.stopPropagation();
      deps.sim.scrub(claim.points.splice(0));
      endClaim();
      return;
    }
    if (claim.kind === 'ballAim' && e.pointerId === claim.pointerId) {
      e.stopPropagation();
      const heldMs = performance.now() - claim.t0;
      const movedPx = Math.hypot(e.clientX - claim.startPx, e.clientY - claim.startPy);
      const g = deps.picker.pickGround(e.clientX, e.clientY);
      if (heldMs < TAP_MS && movedPx < TAP_SLOP_PX) {
        deps.sim.ballPlace(claim.startX, claim.startZ);
        deps.onToolUsed('ball');
      } else if (g) {
        // slingshot: drag vector on the ground = throw velocity
        let vx = (g.x - claim.startX) * 1.6;
        let vz = (g.z - claim.startZ) * 1.6;
        const v = Math.hypot(vx, vz);
        const vMax = 10;
        if (v > vMax) {
          vx = (vx / v) * vMax;
          vz = (vz / v) * vMax;
        }
        deps.sim.ballThrow(vx, vz, Math.min(1, v / vMax));
        deps.sfx.whoosh();
        deps.onToolUsed('ball');
      }
      endClaim();
      return;
    }

    // passive tap
    cancelPress();
    if (e.target !== deps.canvas) {
      tap = null;
      return;
    }
    if (!tap || e.pointerId !== tap.id) return;
    if (e.pointerId === pressConsumedPointer) {
      pressConsumedPointer = -1;
      tap = null;
      return; // the long-press already handled this touch
    }
    const isTap =
      performance.now() - tap.t0 < TAP_MS &&
      Math.hypot(e.clientX - tap.px, e.clientY - tap.py) < TAP_SLOP_PX;
    tap = null;
    if (!isTap) return;

    const tool = deps.getTool();
    const g = deps.picker.pickGround(e.clientX, e.clientY);

    if (tool === 'apple') {
      if (g) {
        deps.sim.dropApple(g.x, g.z);
        deps.sfx.whoosh(); // always lands now — a full meadow rots its oldest apple
        deps.onToolUsed('apple');
      }
      return;
    }
    if (tool === 'sponge') {
      if (g) deps.sim.scrub([g]); // single dab
      return;
    }

    // hand (default): pet a creature, or tap the egg
    const id = pickCreatureAt(e.clientX, e.clientY);
    if (id !== null) {
      deps.sim.pet(id);
      return;
    }
    if (g) {
      const egg = deps.sim.findItem(ITEM.EGG);
      if (egg && Math.hypot(egg.x - g.x, egg.z - g.z) < 2.0) {
        deps.sim.tapEgg(g.x, g.z);
        return;
      }
      // open grass: one tap calls them over, a quick second tap is a song
      const now = performance.now();
      if (
        lastGrassTap &&
        now - lastGrassTap.time < 420 &&
        Math.hypot(e.clientX - lastGrassTap.px, e.clientY - lastGrassTap.py) < 70
      ) {
        deps.sim.song(lastGrassTap.x, lastGrassTap.z);
        deps.onSong(lastGrassTap.x, lastGrassTap.z);
        lastGrassTap = null;
      } else {
        deps.sim.beacon(g.x, g.z);
        deps.onCall(g.x, g.z);
        lastGrassTap = { px: e.clientX, py: e.clientY, x: g.x, z: g.z, time: now };
      }
    }
  };
  deps.root.addEventListener('pointerup', onUp, { capture: true });
  deps.root.addEventListener(
    'pointercancel',
    (e: PointerEvent) => {
      if (claim.kind !== 'none' && 'pointerId' in claim && e.pointerId === claim.pointerId) {
        endClaim();
      }
      cancelPress();
      tap = null;
    },
    { capture: true },
  );
}
